import { ToastNotificationModel } from "Shared/Components/Toast";

import { useCallback } from "react";
import { useToast } from "../../Shared/Hooks/Core/useToast";
import { getQueryRetryReason, isTimeoutError, shouldRetryQuery } from "./queryRetryPolicy";

const retryWarning: ToastNotificationModel = {
    title: "Some requests are slow or experiencing network issue. Please wait for us to retry them.",
    toastType: "warning",
    duration: "Long",
    aggregateType: "RetryNotif"
};

const retriesExhaustedError: ToastNotificationModel = {
    title: "Too many retries are experiencing time out errors.",
    toastType: "error",
    duration: "Long",
    aggregateType: "RetryTimeout"
};

const timeoutError: ToastNotificationModel = {
    title: "Some requests timed out.",
    toastType: "error",
    duration: "Long",
    aggregateType: "RetryTimeout"
};

/** The default `retry` for queries: Core/Query/queryRetryPolicy.ts decides, and timeouts and network errors show a toast. */
export const useQueryRetryHandler = () => {
    const { showToastNotification } = useToast();

    const handler = useCallback(
        (failureCount: number, error: unknown) => {
            const retry = shouldRetryQuery(failureCount, error);
            const reason = getQueryRetryReason(error);
            if (retry && (reason === "timeout" || reason === "network")) {
                showToastNotification(retryWarning);
            } else if (!retry && isTimeoutError(error)) {
                showToastNotification(failureCount > 0 ? retriesExhaustedError : timeoutError);
            }
            return retry;
        },
        [showToastNotification]
    );

    return handler;
};
